import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';

// Eager imports — kept in the initial bundle because they appear on the very
// first paint after auth resolves. Anything users probably won't hit in their
// first ~10 seconds (admin, print, settings, public/portal/agency-site, etc.)
// is lazy-loaded below to keep the main chunk small.

// Layout + auth shell
import { AppLayout } from './components/layout/AppLayout';
import { LoginView } from './pages/LoginView';
import { MfaChallenge, useMfaRequired } from './components/auth/Mfa';
import { ScrollToTop } from './components/ScrollToTop';

// Entry CRM route — eager because Dashboard is the first paint after auth.
import { DashboardPage } from './pages/DashboardPage';
import { NovedadesPage } from './pages/NovedadesPage';
import { NovedadesPostPage } from './pages/NovedadesPostPage';
// PropertiesPage's 5 sub-page exports share a file → splitting individual
// exports gives no real win since the bundler still pulls the whole module.
// Keep them all eager.
import {
  PropertiesListPage,
  PropertyDetailPage,
  PropertyNewPage,
  PropertyEditPage,
  PropertyReportPage,
} from './pages/PropertiesPage';

// Lazy imports — split into separate chunks. React.lazy with named exports
// requires unwrapping via .then(m => ({ default: m.X })). The Suspense boundary
// near the bottom catches them all with a single spinner.
const AgencySubdomainRedirect = lazy(() => import('./pages/agency-site/AgencySubdomainRedirect').then(m => ({ default: m.AgencySubdomainRedirect })));
const SignupPage = lazy(() => import('./pages/public/SignupPage').then(m => ({ default: m.SignupPage })));
const SignupCompletePage = lazy(() => import('./pages/public/SignupCompletePage').then(m => ({ default: m.SignupCompletePage })));
const PlansPage = lazy(() => import('./pages/public/PlansPage').then(m => ({ default: m.PlansPage })));
const CheckoutPage = lazy(() => import('./pages/public/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const InvoicePage = lazy(() => import('./pages/public/InvoicePage').then(m => ({ default: m.InvoicePage })));
const PendingInvoicePage = lazy(() => import('./pages/public/PendingInvoicePage').then(m => ({ default: m.PendingInvoicePage })));
const PaymentLinkPage = lazy(() => import('./pages/public/PaymentLinkPage').then(m => ({ default: m.PaymentLinkPage })));
const FacturaPagadaPage = lazy(() => import('./pages/public/FacturaPagadaPage').then(m => ({ default: m.FacturaPagadaPage })));
const ForgotPasswordPage = lazy(() => import('./pages/public/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/public/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const AcademiaLayout = lazy(() => import('./components/academia/AcademiaLayout').then(m => ({ default: m.AcademiaLayout })));
const AcademiaHomePage = lazy(() => import('./pages/academia/AcademiaHomePage').then(m => ({ default: m.AcademiaHomePage })));
const AcademiaSignupPage = lazy(() => import('./pages/academia/AcademiaSignupPage').then(m => ({ default: m.AcademiaSignupPage })));
// LandingPage: only logged-out visitors on the CRM domain hit it — keep it out
// of the authenticated CRM bundle. LeadsPage (/prospectos): pulls @dnd-kit +
// LeadModal; first paint is Dashboard, not this, so defer it to its own chunk.
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const LeadsPage = lazy(() => import('./pages/LeadsPage').then(m => ({ default: m.LeadsPage })));
const SoportePage = lazy(() => import('./pages/SoportePage').then(m => ({ default: m.SoportePage })));
const PrivacidadPage = lazy(() => import('./pages/PrivacidadPage').then(m => ({ default: m.PrivacidadPage })));
const TerminosPage = lazy(() => import('./pages/TerminosPage').then(m => ({ default: m.TerminosPage })));

const PortalLayout = lazy(() => import('./components/portal/layout/PortalLayout').then(m => ({ default: m.PortalLayout })));
const PortalHomePage = lazy(() => import('./pages/portal/PortalHomePage').then(m => ({ default: m.PortalHomePage })));
const PortalPropertyDetailPage = lazy(() => import('./pages/portal/PortalPropertyDetailPage').then(m => ({ default: m.PortalPropertyDetailPage })));
const SlugResolver = lazy(() => import('./pages/portal/SlugResolver').then(m => ({ default: m.SlugResolver })));
const LegacyPropertyRedirect = lazy(() => import('./pages/portal/LegacyPropertyRedirect').then(m => ({ default: m.LegacyPropertyRedirect })));
const VitrinaPage = lazy(() => import('./pages/VitrinaPage').then(m => ({ default: m.VitrinaPage })));
const WebChatPage = lazy(() => import('./pages/WebChatPage').then(m => ({ default: m.WebChatPage })));
const PropertyReportPublicPage = lazy(() => import('./pages/PropertyReportPublicPage').then(m => ({ default: m.PropertyReportPublicPage })));
const BancoFinanciamientoPage = lazy(() => import('./pages/BancoFinanciamientoPage').then(m => ({ default: m.BancoFinanciamientoPage })));
const RenovarPage = lazy(() => import('./pages/RenovarPage').then(m => ({ default: m.RenovarPage })));
const ContratoPage = lazy(() => import('./pages/ContratoPage').then(m => ({ default: m.ContratoPage })));


const PropertyPrintView = lazy(() => import('./pages/PropertyPrintView').then(m => ({ default: m.PropertyPrintView })));
const OwnersPage = lazy(() => import('./pages/OwnersPage').then(m => ({ default: m.OwnersPage })));
const SpecialZonePage = lazy(() => import('./pages/SpecialZonePage').then(m => ({ default: m.SpecialZonePage })));
const ExchangePropertiesPage = lazy(() => import('./pages/ExchangePropertiesPage').then(m => ({ default: m.ExchangePropertiesPage })));
const ExchangeRequirementsPage = lazy(() => import('./pages/ExchangeRequirementsPage').then(m => ({ default: m.ExchangeRequirementsPage })));
const PropertyMatchesPage = lazy(() => import('./pages/PropertyMatchesPage').then(m => ({ default: m.PropertyMatchesPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage').then(m => ({ default: m.TeamManagementPage })));
const MembershipPage = lazy(() => import('./pages/MembershipPage').then(m => ({ default: m.MembershipPage })));
const RequirementMatchesPage = lazy(() => import('./pages/RequirementMatchesPage').then(m => ({ default: m.RequirementMatchesPage })));
const CoincidenciasHubPage = lazy(() => import('./pages/CoincidenciasHubPage').then(m => ({ default: m.CoincidenciasHubPage })));
const ProjectsListPage = lazy(() => import('./pages/ProjectsPageWrapper').then(m => ({ default: m.ProjectsListPage })));
const ProjectDetailPage = lazy(() => import('./pages/ProjectsPageWrapper').then(m => ({ default: m.ProjectDetailPage })));
const ProjectNewPage = lazy(() => import('./pages/ProjectsPageWrapper').then(m => ({ default: m.ProjectNewPage })));
const ProjectEditPage = lazy(() => import('./pages/ProjectsPageWrapper').then(m => ({ default: m.ProjectEditPage })));
const CalendarPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.CalendarPage })));
const MessagesPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.MessagesPage })));
const ColleaguesPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.ColleaguesPage })));
const ColleagueDetailPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.ColleagueDetailPage })));
const ReportsPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.ReportsPage })));
const SettingsPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.SettingsPage })));
const WebsitePage = lazy(() => import('./pages/WebsitePageWrapper').then(m => ({ default: m.WebsitePage })));
const RequirementsListPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.RequirementsListPage })));
const RequirementNewPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.RequirementNewPage })));
const RequirementEditPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.RequirementEditPage })));
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPageWrapper').then(m => ({ default: m.SuperAdminPage })));
const PlaceholderPage = lazy(() => import('./pages/SimplePageWrappers').then(m => ({ default: m.PlaceholderPage })));
const ReglamentoView = lazy(() => import('./pages/ReglamentoView').then(m => ({ default: m.ReglamentoView })));
const SoporteView = lazy(() => import('./pages/SoporteView').then(m => ({ default: m.SoporteView })));
const SugerenciasView = lazy(() => import('./pages/SugerenciasView').then(m => ({ default: m.SugerenciasView })));
const FinanciamientoView = lazy(() => import('./pages/FinanciamientoView').then(m => ({ default: m.FinanciamientoView })));
const BlogInmobiliarioView = lazy(() => import('./pages/BlogInmobiliarioView').then(m => ({ default: m.BlogInmobiliarioView })));
const AgencyProfileView = lazy(() => import('./pages/admin/AgencyProfileView').then(m => ({ default: m.AgencyProfileView })));
const AgentProfileView = lazy(() => import('./pages/admin/AgentProfileView').then(m => ({ default: m.AgentProfileView })));

// Single shared loader for all Suspense boundaries — matches the auth-loading
// spinner so transitions feel uniform.
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#020617]">
    <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// /intercambio/mensajes merged into the unified inbox — forward the query
// string so ?peer=&property=&requirement=&draft= deep links keep working.
const LegacyIntercambioMensajesRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/mensajes${search}`} replace />;
};

/**
 * Detect if current domain is a portal domain (public-facing)
 * vs the CRM domain (app.intercambioinmobiliario.com / localhost dev)
 */
function isPortalDomain(): boolean {
  const host = window.location.hostname;
  return (
    host.includes('mapainmueble') ||
    host.includes('apartamentosguatemala') ||
    host.includes('casas-guatemala') ||
    host.includes('casasguatemala')
  );
}

function App() {
  const { user, isLoading, isSuspended, isStudent, profile, agency } = useAuth();
  const [mfaRequired, recheckMfa] = useMfaRequired(user?.id);
  const isSuperAdmin = profile?.role === 'super_admin';
  // Mirrors the Sidebar gate. Direct-URL access to /intercambio/* is blocked
  // for agencies with sharing disabled (Super Admin > Agencias > Compartir).
  const sharingDisabled = !isSuperAdmin && agency?.can_share === false;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ========================================
  // PORTAL MODE — Public-facing property portal (portal domains only)
  // ========================================
  if (isPortalDomain()) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteLoader />}>
        <Routes>
          {/* Agency Website routes (per-agency public sites) */}
          <Route path="/agencia/:agencySlug/*" element={<AgencySubdomainRedirect />} />

          {/* Vitrina — standalone branded page (no portal layout) */}
          <Route path="/vitrina/:token" element={<VitrinaPage />} />
          <Route path="/reporte/:token" element={<PropertyReportPublicPage />} />
          <Route path="/banco/:token" element={<BancoFinanciamientoPage />} />
          <Route path="/renovar/:token" element={<RenovarPage />} />
          <Route path="/formulario/:token" element={<ContratoPage />} />
          <Route path="/contrato/:token" element={<ContratoPage />} />

          {/* Portal routes (public, light theme) */}
          <Route element={<PortalLayout />}>
            <Route path="/" element={<PortalHomePage />} />
            <Route path="/propiedades/:slug" element={<PortalPropertyDetailPage />} />
            {/* Legacy URL redirect: /properties/{slug} → /propiedades/{ii_id} */}
            <Route path="/properties/:slug" element={<LegacyPropertyRedirect />} />
            {/* SEO catch-all: handles /casas-en-venta-zona-14/ and /ciudad-cayala/ */}
            <Route path="/:slug" element={<SlugResolver />} />
          </Route>

          {/* Auth & billing pages (shared between portal and CRM) */}
          <Route path="/login" element={<LoginView theme="light" skin="ocean" />} />
          <Route path="/recuperar-contrasena" element={<ForgotPasswordPage />} />
          <Route path="/nueva-contrasena" element={<ResetPasswordPage />} />
          <Route path="/registro" element={<SignupPage />} />
          <Route path="/registro/completado" element={<SignupCompletePage />} />
          <Route path="/planes" element={<PlansPage />} />
          <Route path="/checkout/:planId" element={<CheckoutPage />} />
          <Route path="/factura/inv/:invoiceId" element={<PendingInvoicePage />} />
          <Route path="/factura/:paymentId" element={<InvoicePage />} />
          <Route path="/pagar" element={<PaymentLinkPage />} />
          <Route path="/factura-pagada" element={<FacturaPagadaPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  // ========================================
  // LANDING MODE — SaaS landing page on CRM domain when not logged in
  // ========================================
  if (!user && supabase) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteLoader />}>
        <Routes>
          {/* Landing page — the main marketing page */}
          <Route path="/" element={<LandingPage />} />

          {/* Agency Website routes (accessible from landing too) */}
          <Route path="/agencia/:agencySlug/*" element={<AgencySubdomainRedirect />} />

          {/* Website chat (web-chat edge fn) — public, standalone */}
          <Route path="/chat/:agencySlug" element={<WebChatPage />} />

          {/* Vitrina — standalone branded page */}
          <Route path="/vitrina/:token" element={<VitrinaPage />} />
          <Route path="/reporte/:token" element={<PropertyReportPublicPage />} />
          <Route path="/banco/:token" element={<BancoFinanciamientoPage />} />
          <Route path="/renovar/:token" element={<RenovarPage />} />
          <Route path="/formulario/:token" element={<ContratoPage />} />
          <Route path="/contrato/:token" element={<ContratoPage />} />

          {/* Auth & billing pages */}
          <Route path="/login" element={<LoginView theme="light" skin="ocean" />} />
          <Route path="/recuperar-contrasena" element={<ForgotPasswordPage />} />
          <Route path="/nueva-contrasena" element={<ResetPasswordPage />} />
          <Route path="/registro" element={<SignupPage />} />
          <Route path="/registro/completado" element={<SignupCompletePage />} />
          <Route path="/academia/registro" element={<AcademiaSignupPage />} />
          {/* Public course catalog lands with the lessons migration; until then
              Academia's entry point is the student signup. */}
          <Route path="/academia" element={<Navigate to="/academia/registro" replace />} />
          <Route path="/planes" element={<PlansPage />} />
          <Route path="/soporte" element={<SoportePage />} />
          <Route path="/privacidad" element={<PrivacidadPage />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/checkout/:planId" element={<CheckoutPage />} />
          <Route path="/factura/inv/:invoiceId" element={<PendingInvoicePage />} />
          <Route path="/factura/:paymentId" element={<InvoicePage />} />
          <Route path="/pagar" element={<PaymentLinkPage />} />
          <Route path="/factura-pagada" element={<FacturaPagadaPage />} />

          {/* Deep link target for the Intercambio WhatsApp inquiry (the colega
              sends /propiedades/:id to the property owner). Render the login
              screen AT this URL rather than bouncing to "/": LoginView doesn't
              navigate, so once the session lands App re-renders in CRM MODE and
              the same path resolves to the property. Without this the catch-all
              below eats the link and drops the owner on the landing page. */}
          <Route path="/propiedades/:id" element={<LoginView theme="light" skin="ocean" />} />

          {/* Fallback to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  // Two-step verification pending (super admins with a verified factor, aal1 session).
  if (user && mfaRequired) return <MfaChallenge onVerified={recheckMfa} />;

  // ========================================
  // ACADEMIA MODE — student accounts (role 'student', no agency). They never
  // reach the CRM: every other path lands on the Academia home.
  // ========================================
  if (isStudent) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/nueva-contrasena" element={<ResetPasswordPage />} />
          <Route path="/soporte" element={<SoportePage />} />
          <Route path="/privacidad" element={<PrivacidadPage />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route element={<AcademiaLayout />}>
            <Route path="/academia" element={<AcademiaHomePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/academia" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  // ========================================
  // CRM MODE — Suspended: restrict to membership + payment pages
  // ========================================
  if (isSuspended) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/chat/:agencySlug" element={<WebChatPage />} />
          <Route path="/vitrina/:token" element={<VitrinaPage />} />
          <Route path="/reporte/:token" element={<PropertyReportPublicPage />} />
          <Route path="/banco/:token" element={<BancoFinanciamientoPage />} />
          <Route path="/renovar/:token" element={<RenovarPage />} />
          <Route path="/formulario/:token" element={<ContratoPage />} />
          <Route path="/contrato/:token" element={<ContratoPage />} />

          <Route element={<AppLayout />}>
            <Route path="/membresia" element={<MembershipPage />} />
            <Route path="*" element={<Navigate to="/membresia" replace />} />
          </Route>

          {/* Payment flow pages (no sidebar) */}
          <Route path="/planes" element={<PlansPage />} />
          <Route path="/checkout/:planId" element={<CheckoutPage />} />
          <Route path="/factura/inv/:invoiceId" element={<PendingInvoicePage />} />
          <Route path="/factura/:paymentId" element={<InvoicePage />} />
          <Route path="/pagar" element={<PaymentLinkPage />} />
          <Route path="/factura-pagada" element={<FacturaPagadaPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  // ========================================
  // CRM MODE — Authenticated internal tool
  // ========================================
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Website chat — public page; also reachable while logged in (demos) */}
        <Route path="/chat/:agencySlug" element={<Suspense fallback={<RouteLoader />}><WebChatPage /></Suspense>} />
        {/* Vitrina — standalone branded page, must be BEFORE AppLayout catch-all */}
        <Route path="/vitrina/:token" element={<VitrinaPage />} />
        <Route path="/reporte/:token" element={<PropertyReportPublicPage />} />
        <Route path="/banco/:token" element={<BancoFinanciamientoPage />} />
        <Route path="/renovar/:token" element={<RenovarPage />} />
        <Route path="/formulario/:token" element={<ContratoPage />} />
        <Route path="/contrato/:token" element={<ContratoPage />} />

        <Route element={<AppLayout />}>
          {/* Panel de Control */}
          <Route path="/" element={<DashboardPage />} />

          {/* Novedades — Intercambio Inmobiliario internal blog (show_in_crm) */}
          <Route path="/novedades" element={<NovedadesPage />} />
          <Route path="/novedades/:slug" element={<NovedadesPostPage />} />

          {/* Propiedades */}
          <Route path="/propiedades" element={<PropertiesListPage />} />
          <Route path="/propiedades/nueva" element={<PropertyNewPage />} />
          <Route path="/propiedades/:id" element={<PropertyDetailPage />} />
          <Route path="/propiedades/:id/editar" element={<PropertyEditPage />} />
          <Route path="/propiedades/:id/reporte" element={<PropertyReportPage />} />
          <Route path="/propiedades/:id/imprimir" element={<PropertyPrintView />} />
          <Route path="/propiedades/:id/coincidencias" element={<PropertyMatchesPage />} />

          {/* Requerimiento matches */}
          <Route path="/requerimientos/:id/coincidencias" element={<RequirementMatchesPage />} />

          {/* Coincidencias hub — cross-agency matches landing (gated by can_share like Intercambio) */}
          <Route path="/coincidencias" element={sharingDisabled ? <Navigate to="/" replace /> : <CoincidenciasHubPage />} />

          {/* Intercambio — gated by agency.can_share (Super Admin > Agencias > Compartir). */}
          <Route path="/intercambio/propiedades" element={sharingDisabled ? <Navigate to="/" replace /> : <ExchangePropertiesPage />} />
          <Route path="/intercambio/requerimientos" element={sharingDisabled ? <Navigate to="/" replace /> : <ExchangeRequirementsPage />} />
          {/* Legacy URL — intercambio messages merged into the unified inbox
              (2026-08). Keeps every existing ?peer=&property=&draft= deep link
              (property cards, notifications, colegas) working unchanged. */}
          <Route path="/intercambio/mensajes" element={<LegacyIntercambioMensajesRedirect />} />
          <Route path="/intercambio" element={<Navigate to={sharingDisabled ? "/" : "/intercambio/propiedades"} replace />} />

          {/* Prospectos */}
          <Route path="/prospectos" element={<LeadsPage />} />
          <Route path="/prospectos/:zone" element={<SpecialZonePage />} />

          {/* Propietarios */}
          <Route path="/propietarios" element={<OwnersPage />} />
          <Route path="/propietarios/:zone" element={<SpecialZonePage />} />

          {/* Proyectos */}
          <Route path="/proyectos" element={<ProjectsListPage />} />
          <Route path="/proyectos/nuevo" element={<ProjectNewPage />} />
          <Route path="/proyectos/:id" element={<ProjectDetailPage />} />
          <Route path="/proyectos/:id/editar" element={<ProjectEditPage />} />

          {/* Requerimientos */}
          <Route path="/requerimientos" element={<RequirementsListPage />} />
          <Route path="/requerimientos/nuevo" element={<RequirementNewPage />} />
          <Route path="/requerimientos/:id/editar" element={<RequirementEditPage />} />

          {/* Calendario, Mensajes, Colegas */}
          <Route path="/notificaciones" element={<NotificationsPage />} />
          <Route path="/calendario" element={<CalendarPage />} />
          {/* Omnichannel inbox — agency-scoped via RLS; super_admin sees all agencies. */}
          <Route path="/mensajes" element={<MessagesPage />} />
          <Route path="/colegas" element={<ColleaguesPage />} />
          <Route path="/colegas/:profileId" element={<ColleagueDetailPage />} />

          {/* Reportes y Análisis */}
          <Route path="/reportes" element={<ReportsPage />} />
          <Route path="/analisis" element={<PlaceholderPage title="Análisis e Informes" />} />

          {/* Configuración y Página Web */}
          <Route path="/configuracion" element={<SettingsPage />} />
          <Route path="/pagina-web" element={<WebsitePage />} />

          {/* Membresía */}
          <Route path="/membresia" element={<MembershipPage />} />

          {/* Reglamento */}
          <Route path="/reglamento" element={<ReglamentoView />} />

          {/* Soporte — same path as the public page, which lives in the
              logged-out tree only, so there is no collision. */}
          <Route path="/soporte" element={<SoporteView />} />

          {/* Operación > Financiamiento — Banco G&T Continental applications for clients */}
          <Route path="/financiamiento" element={<FinanciamientoView />} />

          {/* Comunidad — platform-wide feature-request board */}
          <Route path="/sugerencias" element={<SugerenciasView />} />

          {/* Blog Inmobiliario — agency-authored posts for bienesinmuebles.com.gt.
              NOT gated by can_share: an agency with Intercambio switched off can
              still write for the blog. */}
          <Route path="/blog-inmobiliario" element={<BlogInmobiliarioView />} />

          {/* Equipo (Agency admin) */}
          <Route path="/equipo" element={<TeamManagementPage />} />

          {/* Administrador — the SaaS panel is now section-routed
              (/administrador/:section). The two profile deep-dives keep their
              own more-specific routes (React Router ranks them above :section). */}
          <Route path="/administrador" element={<SuperAdminPage />} />
          <Route path="/administrador/agencias/:agencyId" element={<AgencyProfileView />} />
          <Route path="/administrador/agentes/:agentId" element={<AgentProfileView />} />
          <Route path="/administrador/:section" element={<SuperAdminPage />} />

          {/* Legacy V1 (CakeCRM) bookmarks. usage_events still logs these every
              week — 162 hits from 8 users between 2026-07-23 and 08-14 — and the
              catch-all below dumps them all on the dashboard, so a bookmark for
              "Properties" silently loses its destination. Map each to its V2
              equivalent instead. */}
          <Route path="/inicio" element={<Navigate to="/" replace />} />
          <Route path="/inicio/admin/Dashboards" element={<Navigate to="/" replace />} />
          <Route path="/inicio/admin/Properties" element={<Navigate to="/propiedades" replace />} />
          <Route path="/inicio/admin/Sharings/properties" element={<Navigate to="/intercambio/propiedades" replace />} />
          <Route path="/inicio/admin/Leads" element={<Navigate to="/prospectos" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>

        {/* Full-screen pages (no AppLayout sidebar) */}
        <Route path="/nueva-contrasena" element={<ResetPasswordPage />} />
        <Route path="/planes" element={<PlansPage />} />
        <Route path="/checkout/:planId" element={<CheckoutPage />} />
        <Route path="/factura/inv/:invoiceId" element={<PendingInvoicePage />} />
        <Route path="/factura/:paymentId" element={<InvoicePage />} />
        <Route path="/pagar" element={<PaymentLinkPage />} />
        <Route path="/factura-pagada" element={<FacturaPagadaPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
